import { useState, useEffect } from "react";
import { db } from "../firebase/config";
import { collection, query, where, getDocs } from "firebase/firestore";
import { getHoursAgoISO, parseItemDate } from "../utils/dateUtils";

/**
 * useRecentItems — lightweight one-time query hook for dashboard summary data.
 *
 * Queries by childId only (no orderBy) to avoid requiring composite Firestore
 * indexes. Date filtering and sorting are done client-side. This matches the
 * same index-safe pattern used by useFirestore.
 *
 * Re-fetches automatically when the page becomes visible again (handles PWA
 * resume from background where the component doesn't remount).
 *
 * @param {string} collectionName - Firestore collection to query
 * @param {string|null} childId - Child document ID to filter by
 * @param {number} [limitCount=5] - Maximum number of items to return after filtering
 * @param {number} [hoursBack=24] - How many hours back to look for entries
 * @returns {{ items: Array, loading: boolean, error: string|null }}
 */
export function useRecentItems(collectionName, childId, limitCount = 5, hoursBack = 24) {
  // Track the childId that the current state belongs to so we can detect staleness.
  // When childId changes we reset loading to true synchronously during render — before
  // the useEffect fires — so the dashboard gate never sees a false loading state
  // between the moment selectedChild is set and the queries actually starting.
  const [trackedChildId, setTrackedChildId] = useState(childId);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(!!childId);
  const [error, setError] = useState(null);
  // Bumped whenever the page becomes visible — triggers a re-fetch on PWA resume.
  const [visibleAt, setVisibleAt] = useState(0);

  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') setVisibleAt(Date.now());
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  // Synchronous reset during render when childId changes.
  // React allows calling setState during render to trigger a synchronous re-render.
  if (trackedChildId !== childId) {
    setTrackedChildId(childId);
    setItems([]);
    setLoading(!!childId); // true if new childId is real, false if it became null
    setError(null);
  }

  useEffect(() => {
    if (!childId) {
      setItems([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchItems = async () => {
      setLoading(true);
      setError(null);

      try {
        const startISO = getHoursAgoISO(hoursBack);
        const cutoff = new Date(startISO);

        // Query by childId only — no orderBy, no date filter in Firestore.
        // This avoids composite index requirements (same pattern as useFirestore).
        const q = query(
          collection(db, collectionName),
          where("childId", "==", childId)
        );

        const querySnapshot = await getDocs(q);
        const data = [];
        querySnapshot.forEach((doc) => {
          data.push({ id: doc.id, ...doc.data() });
        });

        // Filter and sort client-side
        const filtered = data
          .filter((item) => {
            const d = parseItemDate(item);
            return d && d >= cutoff;
          })
          .sort((a, b) => {
            const da = parseItemDate(a) ?? new Date(0);
            const db_ = parseItemDate(b) ?? new Date(0);
            return db_ - da; // newest first
          })
          .slice(0, limitCount);

        if (!cancelled) {
          setItems(filtered);
          setLoading(false);
        }
      } catch (err) {
        console.error(`useRecentItems error for ${collectionName}:`, err);
        if (!cancelled) {
          setError(err.message);
          setItems([]);
          setLoading(false);
        }
      }
    };

    fetchItems();

    return () => { cancelled = true; };
  }, [collectionName, childId, limitCount, hoursBack, visibleAt]);

  return { items, loading, error };
}
